import { CheckCircleIcon } from '@heroicons/react/20/solid';
import { getProductsByIds } from '@util/firestore/products';
import { OrderDocument } from '@models/order';
import { useState } from 'react';
import ConfirmModal from './ConfirmModal';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import SafeImage from '@ui/SafeImage';
import Spinner from '@ui/Spinner';
import { markAsReceived } from '@util/firestore/orders';
import { logError } from '@util/logError';
import { logEvent } from '@util/analytics';
import { useAuth } from 'context/AuthContext';

const ProductSelect = ({
  order,
  seller_id,
  selectedItems,
  setSelectedItems,
}: {
  order: OrderDocument;
  seller_id: string;
  selectedItems: string[];
  setSelectedItems: (items: string[]) => void;
}) => {
  const { userDoc } = useAuth();
  const { data: products, isLoading } = useQuery({
    queryKey: ['products'],
    queryFn: () =>
      getProductsByIds(
        order.sellers?.[seller_id]?.map((item) => item.id) ?? []
      ),
    enabled: !!order.sellers?.[seller_id],
  });

  if (!order.sellers) return null;

  if (isLoading) {
    return (
      <div className="flex h-[4.4rem] w-[4.4rem] items-center">
        <Spinner />;
      </div>
    );
  }

  const items = userDoc?.roles?.support
    ? order.sellers[seller_id]
    : order.sellers[seller_id].filter((item) => item.state === 2);

  return (
    <>
      {items.map((item) => {
        const product = products?.find((product) => product.id === item.id);
        if (!product) return null;
        return (
          <div className="flex w-full items-center gap-[1.6rem]" key={item.id}>
            <input
              className={`
                    h-[2.4rem] w-[2.4rem] rounded-2xl border-[1px] border-brand-gray
                    `}
              type="checkbox"
              checked={selectedItems.includes(item.id)}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedItems([...selectedItems, item.id]);
                } else {
                  setSelectedItems(
                    selectedItems.filter((id) => id !== item.id)
                  );
                }
              }}
            />
            {/* image and title of the item */}
            <div className="flex items-center gap-[1.6rem]">
              <SafeImage
                src={product.images[0]?.thumb}
                alt={product.title}
                className="h-[6.4rem] w-[6.4rem] rounded-2xl"
              />
              <div className="flex flex-col gap-[0.2rem]">
                <span className="font-semibold leading-[2.2rem] text-brand-lightest-black">
                  {product.title}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};
const MarkAsReceivedModal = ({
  isOpen,
  dismiss,
  order,
  seller_id,
  onSubmit,
}: {
  isOpen: boolean;
  dismiss: () => void;
  order: OrderDocument;
  seller_id: string;
  onSubmit: () => void;
}) => {
  const [loadingMark, setLoadingMark] = useState(false);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const queryClient = useQueryClient();
  return (
    <ConfirmModal
      icon={<CheckCircleIcon width={64} height={64} />}
      title="Mark Items as Received"
      body="Select the items you would like to mark as received. This will finalize the sale and the item can no longer be refunded or returned."
      buttonText="Confirm"
      btnType="success"
      isOpen={isOpen}
      loading={loadingMark}
      disabled={selectedItems.length === 0}
      dismiss={async (confirm) => {
        if (confirm) {
          setLoadingMark(true);
          try {
            await markAsReceived(order.id, selectedItems);
            queryClient.invalidateQueries({
              queryKey: ['order', order.id],
            });

            queryClient.invalidateQueries({
              queryKey: ['orders'],
            });
            setLoadingMark(false);
            onSubmit();
            logEvent('marked_as_received', {
              items_count: selectedItems.length,
              state: order.total_state,
            });
          } catch (e) {
            logError(e);
          } finally {
            setLoadingMark(false);
          }
        } else dismiss();
      }}
    >
      <ProductSelect
        order={order}
        seller_id={seller_id}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
      />
    </ConfirmModal>
  );
};

export default MarkAsReceivedModal;
